import moment from "moment-timezone";
import { IAccessRules, MeetingRoomCalendarDataAccessRules } from "types";
import {
  areaIsTimeRestricted,
  areaIsTimeRestrictedAndRestrictionTimeHasPassed,
  areaHasRestrictedAccessButUserDoesNotHaveAccess,
} from "../area-restriction-rules";

/**
 * Determines if a meeting room is bookable by the current user at the current time,
 * considering access rules and time restrictions
 * @param accessRules - Room access rules
 * @param timezone - Optional timezone to use for "now" calculation (defaults to moment() if not provided)
 */
export const isRoomBookableAtCurrentTime = (
  accessRules?: IAccessRules | MeetingRoomCalendarDataAccessRules | undefined,
  timezone?: string,
): boolean => {
  // If no access rules, room is bookable (default open access)
  if (!accessRules) {
    return true;
  }

  const now = timezone ? moment.tz(timezone) : moment();

  // If user has special access (admin/privileged), room is always bookable
  if (accessRules.userHasAccess) {
    return true;
  }

  // Check if user doesn't have access to a restricted area
  if (areaHasRestrictedAccessButUserDoesNotHaveAccess(accessRules)) {
    return false;
  }

  // Check time restrictions for current time
  if (areaIsTimeRestricted(accessRules)) {
    // For time-restricted rooms, check if restriction time has passed for today
    const today = now.clone();

    // Regular users can book today if it's the current day
    // For time restrictions, we need to check if the restriction time has passed for booking "now"
    if (!areaIsTimeRestrictedAndRestrictionTimeHasPassed(accessRules, today, timezone)) {
      return false;
    }
  }

  // If we get here, the room is bookable
  return true;
};
